<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-start align-center style="padding: 32px">
              <!-- Month Picker -->
              <v-flex xs12 md3>
                <div style="max-width: 290px">
                  <v-menu :close-on-content-click="false" v-model="menu_month_picker" :nudge-right="40" lazy
                    transition="scale-transition" offset-y full-width min-width="290px" max-width="290px"
                    :disabled="loader">
                    <v-text-field slot="activator" v-model="chosen_month" label="Chọn tháng so sánh" :disabled="loader"
                      prepend-icon="event" readonly hide-details></v-text-field>
                    <v-date-picker min="2025-04" :max="maxMonth" locale="vi-VI" type="month" v-model="chosen_month"
                      @input="menu_month_picker = false"></v-date-picker>
                  </v-menu>
                </div>
              </v-flex>
              <v-flex xs12 md6 class="ml-4">
                <div class="caption grey--text">So sánh với tháng: <strong class="primary--text">{{ formatMonthDisplay(previousMonth) }}</strong></div>
              </v-flex>

              <v-flex xs12 md3 class="text-right">
                 <v-btn color="success" @click="exportExcel" :loading="exporting" :disabled="loader || !comparison">
                    <v-icon left small>cloud_download</v-icon>
                    Xuất Excel
                 </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo kết quả kinh doanh (So sánh)
              </v-card-title>
              <v-divider></v-divider>

              <!-- Loading State -->
              <v-card-text v-if="loader" class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="mt-3">Đang tải báo cáo...</p>
              </v-card-text>

              <!-- Report Content -->
              <v-card-text v-else-if="comparison">
                <div class="table-responsive">
                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 280px">Chỉ tiêu</th>
                        <th class="text-right" style="min-width: 150px">{{ formatMonthDisplay(comparison.previous_month) }}</th>
                        <th class="text-right" style="min-width: 150px">{{ formatMonthDisplay(comparison.current_month) }}</th>
                        <th class="text-right" style="min-width: 130px">Chênh lệch</th>
                        <th class="text-right" style="min-width: 100px">Tỷ lệ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- DOANH THU -->
                      <tr class="section-header">
                        <td colspan="5"><strong>DOANH THU</strong></td>
                      </tr>
                      <tr v-for="row in revenueRows" :key="row.key" 
                          :class="{ 
                            'total-row': row.isTotal, 
                            'sub-row': row.isSubItem,
                            'expandable-row': row.isExpandable 
                          }"
                          @click="row.isExpandable ? toggleExpand(row.expandKey) : null">
                        <td :class="row.isSubItem ? 'pl-4' : ''">
                          {{ row.name }}
                          <v-icon v-if="row.isExpandable" small class="ml-1">
                            {{ isExpanded(row.expandKey) ? 'expand_less' : 'expand_more' }}
                          </v-icon>
                        </td>
                        <td class="text-right">
                          {{ formatNegativeValue(row.previous) }}
                        </td>
                        <td class="text-right">
                          {{ formatNegativeValue(row.current) }}
                        </td>
                        <td :class="['text-right', getDiffClass(row.diff)]">
                          {{ formatDiff(row.diff) }}
                        </td>
                        <td :class="['text-right', getDiffClass(row.percent)]">
                          {{ formatPercentage(row.percent) }}
                        </td>
                      </tr>

                      <!-- CHI PHÍ -->
                      <tr class="section-header">
                        <td colspan="5"><strong>CHI PHÍ</strong></td>
                      </tr>
                      <tr v-for="row in expenseRows" :key="row.key" 
                          :class="{ 
                            'total-row': row.isTotal, 
                            'sub-row': row.isSubItem,
                            'expandable-row': row.isExpandable 
                          }"
                          @click="row.isExpandable ? toggleExpand(row.expandKey) : null">
                        <td :class="row.isSubItem ? 'pl-4' : ''">
                          {{ row.name }}
                          <v-icon v-if="row.isExpandable" small class="ml-1">
                            {{ isExpanded(row.expandKey) ? 'expand_less' : 'expand_more' }}
                          </v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(row.previous) }}</td>
                        <td class="text-right">{{ formatExpenseValue(row.current) }}</td>
                        <td :class="['text-right', getDiffClass(-row.diff)]">
                          {{ formatExpenseDiff(row.diff) }}
                        </td>
                        <td :class="['text-right', getDiffClass(-row.percent)]">
                          {{ formatExpensePercentage(row.percent) }}
                        </td>
                      </tr>

                      <!-- LỢI NHUẬN -->
                      <tr class="profit-row">
                        <td><strong>LỢI NHUẬN</strong></td>
                        <td class="text-right">
                          <strong>{{ formatNegativeValue(comparison.previous_data.loi_nhuan) }}</strong>
                        </td>
                        <td class="text-right">
                          <strong>{{ formatNegativeValue(comparison.current_data.loi_nhuan) }}</strong>
                        </td>
                        <td :class="['text-right', getDiffClass(comparison.differences.loi_nhuan.diff)]">
                          <strong>{{ formatDiff(comparison.differences.loi_nhuan.diff) }}</strong>
                        </td>
                        <td :class="['text-right', getDiffClass(comparison.differences.loi_nhuan.percent)]">
                          <strong>{{ formatPercentage(comparison.differences.loi_nhuan.percent) }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>

              <!-- Error State -->
              <v-card-text v-else-if="errorMessage" class="text-center pa-5">
                <v-icon size="60" color="orange">warning</v-icon>
                <p class="mt-3 orange--text">{{ errorMessage }}</p>
              </v-card-text>

              <v-card-text v-else class="text-center pa-5">
                <v-icon size="60" color="grey lighten-1">error_outline</v-icon>
                <p class="mt-3 grey--text">Không có dữ liệu báo cáo</p>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import api from "../../api";
import moment from "moment";
import { ExcelExporter } from "../../helpers/ExcelExporter";
import swal from "sweetalert2";

export default {
  filters: {
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return Number(value).toLocaleString("vi-VN");
    },
  },
  data() {
    return {
      loader: false,
      exporting: false,
      comparison: null,
      errorMessage: null,
      menu_month_picker: false,
      chosen_month: moment().subtract(1, "month").format("YYYY-MM"),
      showSalaryDetail: false,
      showLandRentDetail: false,
      showRoomRevenueDetail: false,
      showReturnDepositDetail: false, // New
      showElecDetail: false, // New
      showWaterDetail: false, // New
      showLoanDetail: false, // New
      showRepairDetail: false, // New
    };
  },
  watch: {
    chosen_month: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo kết quả kinh doanh (So sánh)";
    },
    maxMonth() {
      return moment().format("YYYY-MM");
    },
    previousMonth() {
      return moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
    },
    revenueRows() {
      if (!this.comparison) return [];

      const { current_data, previous_data, differences } = this.comparison;


      const rows = [
        {
          key: "tong_doanh_thu_tien_phong",
          name: "Doanh thu tiền phòng", // Label as requested, but maps to Total Room Revenue
          previous: previous_data.tong_doanh_thu_tien_phong,
          current: current_data.tong_doanh_thu_tien_phong,
          diff: differences.tong_doanh_thu_tien_phong.diff,
          percent: differences.tong_doanh_thu_tien_phong.percent,
          isExpandable: true,
          expandKey: 'roomRevenue',
        },
      ];

      // Add Room Revenue breakdowns
      if (this.showRoomRevenueDetail) {
          const components = [
              { key: 'doanh_thu_tien_phong', name: '- Tiền phòng' },
              { key: 'doanh_thu_tien_dien', name: '- Tiền điện' },
              { key: 'doanh_thu_tien_nuoc', name: '- Tiền nước' },
              { key: 'doanh_thu_tien_rac', name: '- Tiền rác' },
              { key: 'doanh_thu_tien_internet', name: '- Tiền internet' },
              { key: 'doanh_thu_tien_xe_may', name: '- Tiền xe máy' },
              { key: 'doanh_thu_tien_may_giat', name: '- Tiền máy giặt' },
          ];

          components.forEach(comp => {
              if (differences[comp.key]) { // Check if data exists
                  rows.push({
                      key: comp.key,
                      name: comp.name,
                      previous: previous_data[comp.key],
                      current: current_data[comp.key],
                      diff: differences[comp.key].diff,
                      percent: differences[comp.key].percent,
                      isSubItem: true
                  });
              }
          });
      }

      rows.push(
        {
          key: "dieu_chinh_tien_phong",
          name: "Điều chỉnh tiền phòng",
          previous: previous_data.dieu_chinh_tien_phong,
          current: current_data.dieu_chinh_tien_phong,
          diff: differences.dieu_chinh_tien_phong.diff,
          percent: differences.dieu_chinh_tien_phong.percent,
        },
        {
          key: "doanh_thu_tien_coc",
          name: "Doanh thu tiền cọc",
          previous: previous_data.doanh_thu_tien_coc,
          current: current_data.doanh_thu_tien_coc,
          diff: differences.doanh_thu_tien_coc.diff,
          percent: differences.doanh_thu_tien_coc.percent,
        },
        {
          key: "chi_tra_coc",
          name: "Chi trả cọc",
          previous: previous_data.chi_tra_coc,
          current: current_data.chi_tra_coc,
          diff: differences.chi_tra_coc.diff,
          percent: differences.chi_tra_coc.percent,
          isNegative: true,
        },
        {
          key: "thu_khac",
          name: "Thu khác",
          previous: previous_data.thu_khac,
          current: current_data.thu_khac,
          diff: differences.thu_khac.diff,
          percent: differences.thu_khac.percent,
        },
        {
          key: "tong_doanh_thu",
          name: "TỔNG DOANH THU",
          previous: previous_data.tong_doanh_thu,
          current: current_data.tong_doanh_thu,
          diff: differences.tong_doanh_thu.diff,
          percent: differences.tong_doanh_thu.percent,
          isTotal: true,
        },
      );
      
      return rows;
    },
    expenseRows() {
      if (!this.comparison) return [];

      const { current_data, previous_data, differences } = this.comparison;

      const rows = [
        {
          key: "chi_phi_luong_va_quan_ly",
          name: "Chi phí lương và quản lý",
          previous: previous_data.chi_phi_luong_va_quan_ly,
          current: current_data.chi_phi_luong_va_quan_ly,
          diff: differences.chi_phi_luong_va_quan_ly.diff,
          percent: differences.chi_phi_luong_va_quan_ly.percent,
          isExpandable: true,
          expandKey: 'salary',
        },
      ];

      // Add salary details if expanded
      if (this.showSalaryDetail) {
        rows.push(...this.getGenericDetailRows('chi_phi_luong_detail', '- '));
      }

      rows.push({
        key: "chi_phi_thue_dat",
        name: "Chi phí thuê đất",
        previous: previous_data.chi_phi_thue_dat,
        current: current_data.chi_phi_thue_dat,
        diff: differences.chi_phi_thue_dat.diff,
        percent: differences.chi_phi_thue_dat.percent,
        isExpandable: true,
        expandKey: 'landRent',
      });

      // Add land rent details if expanded
      if (this.showLandRentDetail) {
        rows.push(...this.getGenericDetailRows('chi_phi_thue_dat_detail', '- '));
      }

      rows.push(
        {
          key: "chi_phi_dien",
          name: "Chi phí điện",
          previous: previous_data.chi_phi_dien,
          current: current_data.chi_phi_dien,
          diff: differences.chi_phi_dien.diff,
          percent: differences.chi_phi_dien.percent,
          isExpandable: true,
          expandKey: 'electric',
        },
        {
          key: "chi_phi_nuoc",
          name: "Chi phí nước",
          previous: previous_data.chi_phi_nuoc,
          current: current_data.chi_phi_nuoc,
          diff: differences.chi_phi_nuoc.diff,
          percent: differences.chi_phi_nuoc.percent,
          isExpandable: true,
          expandKey: 'water',
        },
        {
          key: "chi_phi_rac_ve_sinh",
          name: "Chi phí rác, vệ sinh",
          previous: previous_data.chi_phi_rac_ve_sinh,
          current: current_data.chi_phi_rac_ve_sinh,
          diff: differences.chi_phi_rac_ve_sinh.diff,
          percent: differences.chi_phi_rac_ve_sinh.percent,
        },
        {
          key: "chi_phi_thue",
          name: "Chi phí thuế",
          previous: previous_data.chi_phi_thue,
          current: current_data.chi_phi_thue,
          diff: differences.chi_phi_thue.diff,
          percent: differences.chi_phi_thue.percent,
        },
        {
          key: "chi_phi_lai_vay",
          name: "Chi phí lãi vay",
          previous: previous_data.chi_phi_lai_vay,
          current: current_data.chi_phi_lai_vay,
          diff: differences.chi_phi_lai_vay.diff,
          percent: differences.chi_phi_lai_vay.percent,
          isExpandable: true,
          expandKey: 'loan',
        },
        {
          key: "chi_phi_quang_cao",
          name: "Chi phí quảng cáo",
          previous: previous_data.chi_phi_quang_cao,
          current: current_data.chi_phi_quang_cao,
          diff: differences.chi_phi_quang_cao.diff,
          percent: differences.chi_phi_quang_cao.percent,
        },
        {
          key: "chi_phi_internet",
          name: "Chi phí Internet",
          previous: previous_data.chi_phi_internet,
          current: current_data.chi_phi_internet,
          diff: differences.chi_phi_internet.diff,
          percent: differences.chi_phi_internet.percent,
        },
        {
          key: "chi_mua_vat_tu_va_sua_chua",
          name: "Chi mua vật tư và sửa chữa",
          previous: previous_data.chi_mua_vat_tu_va_sua_chua,
          current: current_data.chi_mua_vat_tu_va_sua_chua,
          diff: differences.chi_mua_vat_tu_va_sua_chua.diff,
          percent: differences.chi_mua_vat_tu_va_sua_chua.percent,
          isExpandable: true,
          expandKey: 'repair',
        },
        {
          key: "chi_phi_dan_phong",
          name: "Chi phí dẫn phòng",
          previous: previous_data.chi_phi_dan_phong,
          current: current_data.chi_phi_dan_phong,
          diff: differences.chi_phi_dan_phong.diff,
          percent: differences.chi_phi_dan_phong.percent,
        },
        {
          key: "chi_phi_khac",
          name: "Chi phí khác",
          previous: previous_data.chi_phi_khac,
          current: current_data.chi_phi_khac,
          diff: differences.chi_phi_khac.diff,
          percent: differences.chi_phi_khac.percent,
        },
        {
          key: "tong_chi_phi",
          name: "TỔNG CHI PHÍ",
          previous: previous_data.tong_chi_phi,
          current: current_data.tong_chi_phi,
          diff: differences.tong_chi_phi.diff,
          percent: differences.tong_chi_phi.percent,
          isTotal: true,
        }
      );

      // Inject details
      if (this.showElecDetail) {
          const idx = rows.findIndex(r => r.key === 'chi_phi_dien');
          if (idx !== -1) rows.splice(idx + 1, 0, ...this.getGenericDetailRows('chi_phi_dien_detail', '-'));
      }
      if (this.showWaterDetail) {
          const idx = rows.findIndex(r => r.key === 'chi_phi_nuoc');
          if (idx !== -1) rows.splice(idx + 1, 0, ...this.getGenericDetailRows('chi_phi_nuoc_detail', '-'));
      }
      if (this.showLoanDetail) {
          const idx = rows.findIndex(r => r.key === 'chi_phi_lai_vay');
          if (idx !== -1) rows.splice(idx + 1, 0, ...this.getGenericDetailRows('chi_phi_lai_vay_detail', '-'));
      }
      if (this.showRepairDetail) {
          const idx = rows.findIndex(r => r.key === 'chi_mua_vat_tu_va_sua_chua');
          if (idx !== -1) rows.splice(idx + 1, 0, ...this.getGenericDetailRows('chi_mua_vat_tu_detail', '-'));
      }

      return rows;
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatMonthDisplay(month) {
      return moment(month).format("MM/YYYY");
    },
    formatDiff(value) {
      if (value === 0) return "-";
      const formatted = Math.abs(value).toLocaleString("vi-VN");
      return value > 0 ? `${formatted}` : `(${formatted})`;
    },
    getDiffClass(value) {
      if (value < 0) return "negative-value";
      return "";
    },
    isExpanded(key) {
      if (key === 'salary') return this.showSalaryDetail;
      if (key === 'landRent') return this.showLandRentDetail;
      if (key === 'roomRevenue') return this.showRoomRevenueDetail;
      if (key === 'returnDeposit') return this.showReturnDepositDetail;
      if (key === 'electric') return this.showElecDetail;
      if (key === 'water') return this.showWaterDetail;
      if (key === 'loan') return this.showLoanDetail;
      if (key === 'repair') return this.showRepairDetail;
      return false;
    },
    toggleExpand(key) {
      if (key === 'salary') this.showSalaryDetail = !this.showSalaryDetail;
      else if (key === 'landRent') this.showLandRentDetail = !this.showLandRentDetail;
      else if (key === 'roomRevenue') this.showRoomRevenueDetail = !this.showRoomRevenueDetail;
      else if (key === 'returnDeposit') this.showReturnDepositDetail = !this.showReturnDepositDetail;
      else if (key === 'electric') this.showElecDetail = !this.showElecDetail;
      else if (key === 'water') this.showWaterDetail = !this.showWaterDetail;
      else if (key === 'loan') this.showLoanDetail = !this.showLoanDetail;
      else if (key === 'repair') this.showRepairDetail = !this.showRepairDetail;
    },
    formatPercentage(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0%';
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString('vi-VN')}%)`;
      }
      return num.toLocaleString('vi-VN') + '%';
    },
    formatNegativeValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0';
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      }
      return num.toLocaleString('vi-VN');
    },
    formatExpenseValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0';
      if (num > 0) {
        return `(${num.toLocaleString('vi-VN')})`;
      }
      return Math.abs(num).toLocaleString('vi-VN'); // Should not happen for expenses typically, but if negative expense (refund), show as positive text
    },
    formatExpenseDiff(value) {
      if (value === 0) return "-";
      const formatted = Math.abs(value).toLocaleString("vi-VN");
      // For expense diff: > 0 means increased expense (bad/parentheses), < 0 means decreased expense (good/no parentheses)
      return value > 0 ? `(${formatted})` : `${formatted}`;
    },
    formatExpensePercentage(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num === 0) return '0%';
      const formatted = Math.abs(num).toLocaleString('vi-VN');
      // User requested: <0 wrapped in parens (), >0 no parens
      return num < 0 ? `(${formatted}%)` : `${formatted}%`;
    },
    getGenericDetailRows(detail_key, prefix = '-') {
      if (!this.comparison) return [];
      const { current_data, previous_data } = this.comparison;
      const allKeys = new Map();

      const prevList = previous_data[detail_key] || [];
      const currList = current_data[detail_key] || [];

      // Collect all unique items
      prevList.filter(d => !d.hidden).forEach(d => allKeys.set(d.key, d.text || d.name || d.key));
      currList.filter(d => !d.hidden).forEach(d => allKeys.set(d.key, d.text || d.name || d.key));

      return Array.from(allKeys.entries()).map(([key, text]) => {
        const prevItem = prevList.find(d => d.key === key);
        const currItem = currList.find(d => d.key === key);
        const prevAmount = prevItem ? (prevItem.amount || prevItem.total_amount || 0) : 0;
        const currAmount = currItem ? (currItem.amount || currItem.total_amount || 0) : 0;
        
        const diff = currAmount - prevAmount;
        let percent = 0;
        if (prevAmount !== 0) {
           percent = ((diff / Math.abs(prevAmount)) * 100).toFixed(0);
        } else if (currAmount !== 0) {
           percent = 100;
        }

        return {
          key: `${detail_key}_${key}`,
          name: `${prefix} ${text}`,
          previous: prevAmount,
          current: currAmount,
          diff: diff,
          percent: parseFloat(percent),
          isSubItem: true
        };
      });
    },
    getLoanDetailRows() {
      if (!this.comparison) return [];
      const { current_data, previous_data } = this.comparison;
      const detail_key = 'chi_phi_lai_vay_detail';
      const prevList = previous_data[detail_key] || [];
      const currList = current_data[detail_key] || [];
      
      const rows = [];
      const parentKeys = new Set();
      // Identify all parents
      prevList.forEach(d => parentKeys.add(d.key));
      currList.forEach(d => parentKeys.add(d.key));
      
      parentKeys.forEach(parentKey => {
         const prevParent = prevList.find(d => d.key === parentKey);
         const currParent = currList.find(d => d.key === parentKey);
         
         const name = (prevParent && prevParent.text) || (currParent && currParent.text) || parentKey;
         const prevAmt = (prevParent && prevParent.amount) || 0;
         const currAmt = (currParent && currParent.amount) || 0;
         const diff = currAmt - prevAmt;
         
         let percent = 0;
         if (prevAmt !== 0) {
            percent = ((diff / Math.abs(prevAmt)) * 100).toFixed(0);
         } else if (currAmt !== 0) {
            percent = 100;
         }

         if (name !== 'Khác') {
             rows.push({
                 key: `loan_${parentKey}`,
                 name: `${name}`,
                 previous: prevAmt,
                 current: currAmt,
                 diff,
                 percent: parseFloat(percent),
                 isSubItem: true
             });
         }
         
         // Sub-details
         // Merge sub-keys
         const subKeys = new Set();
         if (prevParent && prevParent.details) prevParent.details.forEach(d => subKeys.add(d.key || d.text));
         if (currParent && currParent.details) currParent.details.forEach(d => subKeys.add(d.key || d.text));
         
         subKeys.forEach(subKey => {
             const prevSub = prevParent && prevParent.details && prevParent.details.find(d => (d.key || d.text) === subKey);
             const currSub = currParent && currParent.details && currParent.details.find(d => (d.key || d.text) === subKey);
             const subName = (prevSub && prevSub.text) || (currSub && currSub.text) || subKey;

             if (subName === 'Khác') return;

             const pSubAmt = (prevSub && prevSub.amount) || 0;
             const cSubAmt = (currSub && currSub.amount) || 0;
             const sDiff = cSubAmt - pSubAmt;
             
             let sPercent = 0;
             if (pSubAmt !== 0) {
                sPercent = ((sDiff / Math.abs(pSubAmt)) * 100).toFixed(0);
             } else if (cSubAmt !== 0) {
                sPercent = 100;
             }
             
             rows.push({
                 key: `loan_${parentKey}_${subKey}`,
                 name: `${subName}`,
                 previous: pSubAmt,
                 current: cSubAmt,
                 diff: sDiff,
                 percent: parseFloat(sPercent),
                 isSubItem: true,
                 isSubSubItem: true
             });
         });
      });
      return rows;
    },
    async getData() {
      this.loader = true;
      this.comparison = null;
      this.errorMessage = null;

      try {
        const result = await api.getComparisonBusinessReport(`month=${this.chosen_month}`);

        if (result.status === 200) {
          this.comparison = result.data;
        } else {
          this.errorMessage = result.message || "Không thể tải báo cáo";
        }
      } catch (error) {
        console.error("Error loading report:", error);
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || "Không thể tải báo cáo";
      }

      this.loader = false;
    },
    async exportExcel() {
        if (!this.comparison) return;
        this.exporting = true;
        try {
            const { current_data, previous_data, differences } = this.comparison;
            const prevMonthText = this.formatMonthDisplay(this.previousMonth);
            const currMonthText = this.formatMonthDisplay(this.chosen_month);

            const columns = [
                { header: 'Chỉ tiêu', key: 'name', width: 40 },
                { header: prevMonthText, key: 'previous', width: 20, align: 'right', format: 'currency' },
                { header: currMonthText, key: 'current', width: 20, align: 'right', format: 'currency' },
                { header: 'Chênh lệch', key: 'diff', width: 20, align: 'right', format: 'currency' },
                { header: 'Tỷ lệ', key: 'percent', width: 15, align: 'right' }
            ];

            const data = [];
            const addRow = (name, prev, curr, dif, pct, style = {}) => {
                data.push({
                    name,
                    previous: prev,
                    current: curr,
                    diff: dif,
                    percent: this.formatExpensePercentage(pct),
                    style,
                    outlineLevel: style.indent
                });
            };

            // --- REVENUE ---
            data.push({ name: 'DOANH THU', style: { bold: true, background: 'FFE3F2FD' } });

            // Room Revenue (Force Expand)
            addRow('Doanh thu tiền phòng', previous_data.tong_doanh_thu_tien_phong, current_data.tong_doanh_thu_tien_phong, differences.tong_doanh_thu_tien_phong.diff, differences.tong_doanh_thu_tien_phong.percent);
            
            // Sub items
            const revComponents = [
              { key: 'doanh_thu_tien_phong', name: 'Tiền phòng' },
              { key: 'doanh_thu_tien_dien', name: 'Tiền điện' },
              { key: 'doanh_thu_tien_nuoc', name: 'Tiền nước' },
              { key: 'doanh_thu_tien_rac', name: 'Tiền rác' },
              { key: 'doanh_thu_tien_internet', name: 'Tiền internet' },
              { key: 'doanh_thu_tien_xe_may', name: 'Tiền xe máy' },
              { key: 'doanh_thu_tien_may_giat', name: 'Tiền máy giặt' },
            ];
            revComponents.forEach(comp => {
                if (differences[comp.key]) {
                    addRow(comp.name, previous_data[comp.key], current_data[comp.key], differences[comp.key].diff, differences[comp.key].percent, { indent: 1 });
                }
            });

            // Adjust
            addRow('Điều chỉnh tiền phòng', previous_data.dieu_chinh_tien_phong, current_data.dieu_chinh_tien_phong, differences.dieu_chinh_tien_phong.diff, differences.dieu_chinh_tien_phong.percent);
            
            // Deposit
            addRow('Doanh thu tiền cọc', previous_data.doanh_thu_tien_coc, current_data.doanh_thu_tien_coc, differences.doanh_thu_tien_coc.diff, differences.doanh_thu_tien_coc.percent);
            
            // Return Deposit (Negative flow)
            // UI shows negative with parens. Excel needs negative number. 
            // `previous_data.chi_tra_coc` is likely negative in data? 
            // Let's check `formatNegativeValue` again. `num < 0 ? (val)`.
            // So if data is -500, UI shows (500).
            // Excel: -500 shows (500) if currency format.
            // So just pass the value directly.
            addRow('Chi trả cọc', previous_data.chi_tra_coc, current_data.chi_tra_coc, differences.chi_tra_coc.diff, differences.chi_tra_coc.percent);

            addRow('Thu khác', previous_data.thu_khac, current_data.thu_khac, differences.thu_khac.diff, differences.thu_khac.percent);
            
            // Total Revenue
            addRow('TỔNG DOANH THU', previous_data.tong_doanh_thu, current_data.tong_doanh_thu, differences.tong_doanh_thu.diff, differences.tong_doanh_thu.percent, { bold: true, background: 'FFFFF3E0' });


            // --- EXPENSES ---
            data.push({ name: 'CHI PHÍ', style: { bold: true, background: 'FFE3F2FD' } });

            // Helper for Expense (Negate positive values to show as expense)
            const addExpenseRow = (name, prev, curr, dif, pct, style = {}) => {
                // prev/curr are positive expenses. Negate them.
                // dif: `formatExpenseDiff` > 0 => increased expense => bad.
                // In Excel: -110 vs -100. Diff is -10 (more negative).
                // If I pass -110, -100. Diff = -10.
                // `formatExpenseDiff` takes `dif` from comparison which is `curr - prev`. 110 - 100 = 10.
                // If I negate amounts: -110 - (-100) = -10.
                // So I should negate `prev` and `curr`.
                // `dif`: Should I negate `dif`? 
                // DB Diff: 10. Excel Diff (-110 - -100) = -10.
                // So yes, negate `dif` too.
                data.push({
                    name,
                    previous: prev ? -prev : 0,
                    current: curr ? -curr : 0,
                    diff: dif ? -dif : 0,
                    percent: pct < 0 ? `(${Math.abs(pct)}%)` : `${pct}%`,
                    style,
                    outlineLevel: style.indent
                });
            };

            // 1. Salary
            addExpenseRow('Chi phí lương và quản lý', previous_data.chi_phi_luong_va_quan_ly, current_data.chi_phi_luong_va_quan_ly, differences.chi_phi_luong_va_quan_ly.diff, differences.chi_phi_luong_va_quan_ly.percent, { indent: 0 }); // indent 0 or 1? UI puts it at root level.
            // Details
            const salaryDetails = this.getGenericDetailRows('chi_phi_luong_detail', '');
            salaryDetails.forEach(d => {
                 addRow(d.name, d.previous ? -d.previous : 0, d.current ? -d.current : 0, d.diff ? -d.diff : 0, d.percent, { indent: 1 });
            });

            // 2. Land Rent
            addExpenseRow('Chi phí thuê đất', previous_data.chi_phi_thue_dat, current_data.chi_phi_thue_dat, differences.chi_phi_thue_dat.diff, differences.chi_phi_thue_dat.percent);
            this.getGenericDetailRows('chi_phi_thue_dat_detail', '').forEach(d => addRow(d.name, -d.previous, -d.current, -d.diff, d.percent, { indent: 1 }));

            // 3. Electric
            addExpenseRow('Chi phí điện', previous_data.chi_phi_dien, current_data.chi_phi_dien, differences.chi_phi_dien.diff, differences.chi_phi_dien.percent);
            this.getGenericDetailRows('chi_phi_dien_detail', '').forEach(d => addRow(d.name, -d.previous, -d.current, -d.diff, d.percent, { indent: 1 }));

            // 4. Water
            addExpenseRow('Chi phí nước', previous_data.chi_phi_nuoc, current_data.chi_phi_nuoc, differences.chi_phi_nuoc.diff, differences.chi_phi_nuoc.percent);
            this.getGenericDetailRows('chi_phi_nuoc_detail', '').forEach(d => addRow(d.name, -d.previous, -d.current, -d.diff, d.percent, { indent: 1 }));

            addExpenseRow('Chi phí rác, vệ sinh', previous_data.chi_phi_rac_ve_sinh, current_data.chi_phi_rac_ve_sinh, differences.chi_phi_rac_ve_sinh.diff, differences.chi_phi_rac_ve_sinh.percent);
            addExpenseRow('Chi phí thuế', previous_data.chi_phi_thue, current_data.chi_phi_thue, differences.chi_phi_thue.diff, differences.chi_phi_thue.percent);

            // 7. Loan
            addExpenseRow('Chi phí lãi vay', previous_data.chi_phi_lai_vay, current_data.chi_phi_lai_vay, differences.chi_phi_lai_vay.diff, differences.chi_phi_lai_vay.percent);
            this.getLoanDetailRows().forEach(d => addRow(d.name, -d.previous, -d.current, -d.diff, d.percent, { indent: d.isSubSubItem ? 2 : 1 }));

            addExpenseRow('Chi phí quảng cáo', previous_data.chi_phi_quang_cao, current_data.chi_phi_quang_cao, differences.chi_phi_quang_cao.diff, differences.chi_phi_quang_cao.percent);
            addExpenseRow('Chi phí Internet', previous_data.chi_phi_internet, current_data.chi_phi_internet, differences.chi_phi_internet.diff, differences.chi_phi_internet.percent);

            // 10. Repair
            addExpenseRow('Chi mua vật tư và sửa chữa', previous_data.chi_mua_vat_tu_va_sua_chua, current_data.chi_mua_vat_tu_va_sua_chua, differences.chi_mua_vat_tu_va_sua_chua.diff, differences.chi_mua_vat_tu_va_sua_chua.percent);
            this.getGenericDetailRows('chi_mua_vat_tu_detail', '').forEach(d => addRow(d.name, -d.previous, -d.current, -d.diff, d.percent, { indent: 1 }));

            addExpenseRow('Chi phí dẫn phòng', previous_data.chi_phi_dan_phong, current_data.chi_phi_dan_phong, differences.chi_phi_dan_phong.diff, differences.chi_phi_dan_phong.percent);
            addExpenseRow('Chi phí khác', previous_data.chi_phi_khac, current_data.chi_phi_khac, differences.chi_phi_khac.diff, differences.chi_phi_khac.percent);

            // Total Expense
            addExpenseRow('TỔNG CHI PHÍ', previous_data.tong_chi_phi, current_data.tong_chi_phi, differences.tong_chi_phi.diff, differences.tong_chi_phi.percent, { bold: true, background: 'FFFFF3E0' });


            // --- PROFIT ---
            data.push({ name: 'LỢI NHUẬN', style: { bold: true, background: 'FFE8F5E9' } });
            addRow('Lợi nhuận', previous_data.loi_nhuan, current_data.loi_nhuan, differences.loi_nhuan.diff, differences.loi_nhuan.percent, { bold: true });


            await ExcelExporter.exportReport({
                title: 'BÁO CÁO KẾT QUẢ KINH DOANH SO SÁNH',
                subtitle: `${prevMonthText} - ${currMonthText}`,
                columns: columns,
                data: data,
                fileName: `BaoCaoSoSanhKQKD_${this.chosen_month}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table th,
.comparison-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.comparison-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.comparison-table .section-header {
  background-color: #e3f2fd;
}

.comparison-table .total-row {
  background-color: #fff3e0;
  font-weight: bold;
}

.comparison-table .profit-row {
  background-color: #e8f5e9;
}

.comparison-table .sub-row {
  background-color: #fafafa;
}

.comparison-table .expandable-row {
  cursor: pointer;
}

.comparison-table .expandable-row:hover {
  background-color: #f0f0f0;
}

.comparison-table tbody tr:hover {
  background-color: #fafafa;
}

.pl-4 {
  padding-left: 32px !important;
}


</style>
